// ═══════════════════════════════════════════════════════════════════════════════
// Reusable comment thread (list + composer)
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { commentApi } from '../../lib/api';
import { emitCommentLimitIfReached } from '../../lib/limitError';
import {
  AutoTranslationHint,
  resolveTranslatedDisplayText,
  translationLanguageNameKey,
} from '../translation';
import { CommentComposerModal } from './CommentComposerModal';
import { LEVEL1_DEFAULTS, USER_ROLE, type CommentTargetType, type CommentWithUser } from '@fdh/shared';

type CommentThreadProps = {
  targetType: CommentTargetType;
  targetId: string;
  readOnly?: boolean;
};

export type CommentThreadHandle = {
  hasUnsentDraft: () => boolean;
  /** Returns true if nothing to send or send succeeded. */
  sendDraft: () => Promise<boolean>;
  clearDraft: () => void;
  /** Opens the comment composer modal (e.g. after unsent-comment dialog "back to edit"). */
  openComposer: () => void;
};

const getAvatarLetter = (nickname: string): string => {
  const trimmed = nickname.trim();
  if (trimmed.length === 0) return '?';
  return trimmed.charAt(0).toUpperCase();
};

const getAvatarColorClassName = (isSelfComment: boolean): string => {
  if (isSelfComment) {
    return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200';
  }
  return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200';
};

export const CommentThread = forwardRef<CommentThreadHandle, CommentThreadProps>(
  ({ targetType, targetId, readOnly = false }, ref) => {
  const { t, language } = useLanguage();
  const { user } = useAuth();

  const [comments, setComments] = useState<CommentWithUser[]>([]);
  const [maxComments, setMaxComments] = useState<number>(LEVEL1_DEFAULTS.MAX_COMMENTS_PER_TARGET);
  const [maxCommentLength, setMaxCommentLength] = useState<number>(LEVEL1_DEFAULTS.MAX_COMMENT_LENGTH);
  const [remainingComments, setRemainingComments] = useState<number>(
    LEVEL1_DEFAULTS.MAX_COMMENTS_PER_TARGET,
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [composerOpen, setComposerOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [showOriginalByCommentId, setShowOriginalByCommentId] = useState<Record<string, boolean>>({});
  const draftRef = useRef('');

  draftRef.current = draft;

  const fetchComments = useCallback(async () => {
    if (!targetId) return;

    setLoading(true);
    setLoadError(null);

    try {
      const response = await commentApi.list(targetType, targetId, language);
      if (response.success && response.data) {
        setComments(response.data.comments);
        setMaxComments(response.data.maxComments);
        setMaxCommentLength(response.data.maxCommentLength);
        setRemainingComments(response.data.remainingComments);
      } else {
        setLoadError(response.error ?? t('comment.loadError'));
      }
    } catch {
      setLoadError(t('comment.loadError'));
    } finally {
      setLoading(false);
    }
  }, [targetType, targetId, t, language]);

  useEffect(() => {
    setShowOriginalByCommentId({});
  }, [language]);

  useEffect(() => {
    void fetchComments();
  }, [fetchComments]);

  const handleDraftChange = (value: string): void => {
    setDraft(value);
    setSendError(null);
  };

  const postComment = useCallback(
    async (content: string): Promise<boolean> => {
      if (!targetId) {
        return true;
      }

      setSending(true);
      setSendError(null);

      try {
        const response = await commentApi.create({
          targetType,
          targetId,
          content,
        });

        if (response.success && response.data) {
          setDraft('');
          setComposerOpen(false);
          await fetchComments();
          return true;
        }
        setSendError(response.error ?? t('comment.sendError'));
        return false;
      } catch (err) {
        const message = err instanceof Error ? err.message : '';
        if (message.includes('429')) {
          setSendError(t('comment.limitReached'));
        } else {
          setSendError(message || t('comment.sendError'));
        }
        return false;
      } finally {
        setSending(false);
      }
    },
    [fetchComments, targetId, targetType, t],
  );

  const handleSend = useCallback(async (): Promise<void> => {
    if (!targetId || sending || remainingComments <= 0) {
      return;
    }

    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      setSendError(t('comment.emptyDraft'));
      return;
    }

    if (trimmed.length > maxCommentLength) {
      setSendError(t('comment.tooLong', { max: maxCommentLength }));
      return;
    }

    await postComment(trimmed);
  }, [draft, maxCommentLength, postComment, remainingComments, sending, targetId, t]);

  const handleOpenComposer = useCallback((): void => {
    if (loading) {
      return;
    }
    if (emitCommentLimitIfReached(comments.length, maxComments)) {
      return;
    }
    setSendError(null);
    setComposerOpen(true);
  }, [comments.length, loading, maxComments]);

  const handleCloseComposer = useCallback((): void => {
    if (sending) {
      return;
    }
    setComposerOpen(false);
  }, [sending]);

  const sendDraftForSubmit = useCallback(async (): Promise<boolean> => {
    if (!targetId) {
      return true;
    }
    if (sending) {
      return false;
    }

    const trimmed = draftRef.current.trim();
    if (trimmed.length === 0) {
      return true;
    }
    if (remainingComments <= 0) {
      emitCommentLimitIfReached(comments.length, maxComments);
      setSendError(t('comment.limitReached'));
      return false;
    }
    if (trimmed.length > maxCommentLength) {
      setSendError(t('comment.tooLong', { max: maxCommentLength }));
      return false;
    }
    return postComment(trimmed);
  }, [comments.length, maxCommentLength, maxComments, postComment, remainingComments, sending, targetId, t]);

  useImperativeHandle(
    ref,
    () => ({
      hasUnsentDraft: () => draftRef.current.trim().length > 0,
      sendDraft: () => sendDraftForSubmit(),
      clearDraft: () => {
        setDraft('');
        setSendError(null);
        setComposerOpen(false);
      },
      openComposer: () => {
        setSendError(null);
        setComposerOpen(true);
      },
    }),
    [sendDraftForSubmit],
  );

  if (!targetId) {
    return null;
  }

  const employerUnpaired =
    user?.role === USER_ROLE.EMPLOYER && !(user.pairedAt && user.pairedAt.trim() !== '');
  const commentsLocked = Boolean(readOnly) || employerUnpaired;
  const usedCount = comments.length;
  const overLimit = draft.length > maxCommentLength;
  const hasDraft = draft.trim().length > 0;
  const canSend =
    !commentsLocked &&
    !loading &&
    remainingComments > 0 &&
    !sending &&
    hasDraft &&
    !overLimit;

  return (
    <section
      className="rounded-xl bg-white p-4 shadow-sm dark:bg-gray-900 dark:shadow-black/20"
      aria-labelledby="comment-section-heading"
    >
      <h3
        id="comment-section-heading"
        className="mb-3 text-sm font-medium text-gray-700 dark:text-gray-200"
      >
        {t('comment.sectionTitle')}
      </h3>

      <p className="mb-3 text-xs text-gray-500 dark:text-gray-400" aria-live="polite">
        {t('comment.usage', { used: usedCount, max: maxComments })}
      </p>

      {loadError && (
        <p className="mb-3 text-sm text-red-600 dark:text-red-400" role="alert">
          {loadError}
        </p>
      )}

      {loading && !loadError ? (
        <div className="flex justify-center py-6" aria-busy="true">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600 dark:border-primary-800 dark:border-t-primary-400" />
        </div>
      ) : (
        <ul className="mb-4 space-y-3" aria-label={t('comment.listAria')}>
          {comments.length === 0 ? (
            <li className="text-sm italic text-gray-500 dark:text-gray-400">{t('comment.empty')}</li>
          ) : (
            comments.map((comment) => {
              const isSelfComment = comment.userId === user?.id;
              const avatarColorClassName = getAvatarColorClassName(isSelfComment);
              const hasTranslation = Boolean(!isSelfComment && comment.translation?.hasTranslation);
              const showOriginal = showOriginalByCommentId[comment.id] ?? false;
              const targetLanguageLabel = comment.translation?.targetLanguage
                ? t(translationLanguageNameKey(comment.translation.targetLanguage))
                : '';
              const displayContent = resolveTranslatedDisplayText(
                comment.content,
                comment.translation?.fields.content,
                showOriginal,
              );
              const handleToggleCommentOriginal = (): void => {
                setShowOriginalByCommentId((prev) => ({
                  ...prev,
                  [comment.id]: !(prev[comment.id] ?? false),
                }));
              };
              return (
              <li
                key={comment.id}
                className="flex gap-3 border-b border-gray-100 pb-3 last:border-0 dark:border-gray-700"
              >
                <div
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${avatarColorClassName}`}
                  aria-hidden="true"
                >
                  {getAvatarLetter(comment.userNickname)}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {comment.userNickname}
                    </span>
                    <div className="flex items-center gap-1">
                      <time
                        className="text-xs text-gray-500 dark:text-gray-400"
                        dateTime={comment.createdAt.replace(' ', 'T')}
                      >
                        {comment.createdAt}
                      </time>
                      {hasTranslation ? (
                        <AutoTranslationHint
                          targetLanguageLabel={targetLanguageLabel}
                          showingOriginal={showOriginal}
                          onToggleOriginal={handleToggleCommentOriginal}
                        />
                      ) : null}
                    </div>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap break-words text-sm text-gray-700 dark:text-gray-200">
                    {displayContent}
                  </p>
                </div>
              </li>
              );
            })
          )}
        </ul>
      )}

      {remainingComments <= 0 && !loading && !loadError && (
        <p className="mb-2 text-sm text-amber-800 dark:text-amber-200" role="status">
          {t('comment.limitReached')}
        </p>
      )}

      {!loadError && remainingComments > 0 && !commentsLocked && (
        <div className="space-y-2 border-t border-gray-100 pt-3 dark:border-gray-700">
          <button
            type="button"
            onClick={handleOpenComposer}
            disabled={loading || sending}
            className="flex min-h-11 w-full items-center justify-between gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-3 text-left text-sm font-medium text-primary-700 transition-colors hover:border-primary-300 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800/60 dark:text-primary-300 dark:hover:border-primary-600 dark:hover:bg-gray-800"
            aria-label={t('comment.writeButton')}
          >
            <span className="flex min-w-0 items-center gap-2">
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-primary-500 bg-primary-100 text-primary-700 dark:border-primary-500 dark:bg-primary-900/50 dark:text-primary-300"
                aria-hidden="true"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </span>
              <span>{t('comment.writeButton')}</span>
            </span>
            {hasDraft ? (
              <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-100">
                {t('comment.draftPending')}
              </span>
            ) : null}
          </button>
          {sendError && !composerOpen ? (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {sendError}
            </p>
          ) : null}
        </div>
      )}

      <CommentComposerModal
        open={composerOpen}
        title={t('comment.modalTitle')}
        placeholder={t('comment.placeholder')}
        draft={draft}
        maxCommentLength={maxCommentLength}
        sending={sending}
        sendError={sendError}
        canSend={canSend}
        charCountLabel={t('comment.charCount', { current: draft.length, max: maxCommentLength })}
        shortcutHint={t('comment.shortcutHint')}
        sendLabel={t('comment.send')}
        sendingLabel={t('comment.sending')}
        closeLabel={t('common.close')}
        sendAriaLabel={t('comment.sendAria')}
        onDraftChange={handleDraftChange}
        onSend={() => void handleSend()}
        onClose={handleCloseComposer}
      />
    </section>
  );
  },
);

CommentThread.displayName = 'CommentThread';
